(function() {
  'use strict';

  /* ============================================
     Wishlist Page - Sidebar Layout Implementation
     ============================================ */

  // === Configuration ===
  const CONFIG = {
    apiUrl: '/apps/wishist',
    selectors: {
      config: '.gf-wishlist-page-config',
      container: '#gf-wishlist-page',
      sidebar: '#gf-wishlist-sidebar',
      wishlistList: '#gf-wishlist-list',
      savedLaterList: '#gf-saved-later-list',
      savedLaterSection: '#gf-saved-later-section',
      mainContent: '#gf-wishlist-main-content',
      productsWrapper: '.gf-wishlist-products-wrapper',
      productsTitle: '#gf-wishlist-products-title',
      productsGrid: '#gf-wishlist-products-grid',
      deleteAllBtn: '#gf-wishlist-delete-all',
      states: {
        loading: '[data-state="loading"]',
        empty: '[data-state="empty"]',
        error: '[data-state="error"]',
        main: '[data-state="main"]'
      }
    }
  };

  // === DOM Elements ===
  const elements = {
    config: document.querySelector(CONFIG.selectors.config),
    container: document.querySelector(CONFIG.selectors.container),
    sidebar: document.querySelector(CONFIG.selectors.sidebar),
    wishlistList: document.querySelector(CONFIG.selectors.wishlistList),
    savedLaterList: document.querySelector(CONFIG.selectors.savedLaterList),
    savedLaterSection: document.querySelector(CONFIG.selectors.savedLaterSection),
    mainContent: document.querySelector(CONFIG.selectors.mainContent),
    productsWrapper: document.querySelector(CONFIG.selectors.productsWrapper),
    productsTitle: document.querySelector(CONFIG.selectors.productsTitle),
    productsGrid: document.querySelector(CONFIG.selectors.productsGrid),
    deleteAllBtn: document.querySelector(CONFIG.selectors.deleteAllBtn),
    states: {
      loading: document.querySelector(CONFIG.selectors.states.loading),
      empty: document.querySelector(CONFIG.selectors.states.empty),
      error: document.querySelector(CONFIG.selectors.states.error)
    }
  };

  // === State Management ===
  const state = {
    currentWishlistName: null,
    currentWishlistId: null,
    currentSection: 'wishlists', // 'wishlists' or 'saved-for-later'
    wishlists: {},
    products: [], // Regular wishlist products
    images: {},
    savedForLaterProducts: [],
    savedForLaterCount: 0,
    wishlistProductsLoaded: false, // Track if wishlist products are loaded
    settings: {},
    selectedCheckbox: {}, // For compare products feature
    compareModalOpen: false,
    shareModalOpen: false, // For share wishlist modal
    shareFormData: {
      senderName: '',
      recipientsMail: '',
      message: '',
      reCaptchaValue: ''
    },
    reCaptchaWidgetId: null
  };

  // === Helper Functions ===
  const getShopDomain = () => {
    return typeof Shopify !== 'undefined' ? Shopify.shop : window.location.hostname;
  };

  const getCustomerId = () => {
    // Try multiple ways to get customer ID
    if (typeof window.meta?.page?.customerId !== 'undefined') {
      return window.meta.page.customerId;
    }
    // Check if customer is logged in via Shopify theme object
    if (typeof window.Shopify?.customer !== 'undefined' && window.Shopify.customer) {
      return window.Shopify.customer.id || null;
    }
    // Check for customer ID in meta tags
    const customerMeta = document.querySelector('meta[name="customer-id"]');
    if (customerMeta) {
      return customerMeta.getAttribute('content') || null;
    }
    return null;
  };

  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  };

  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  function injectCustomCss(css) {
    try {
      if (css === null || css === undefined) return;
      const cssText = String(css).trim();
      if (!cssText) return;

      let styleEl = document.getElementById('gf-wishlist-custom-css');
      if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = 'gf-wishlist-custom-css';
        styleEl.type = 'text/css';
        document.head.appendChild(styleEl);
      }
      styleEl.textContent = cssText;
    } catch (error) {
      console.error('Failed to inject custom CSS:', error);
    }
  }

  // === API Functions ===
  async function apiCall(endpoint, data = {}) {
    try {
      const response = await fetch(`${CONFIG.apiUrl}/${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          shop_domain: getShopDomain(),
          customer_id: getCustomerId(),
          session_id: getCookie('gemfind_sessionId'),
          ...data
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API call failed:', error);
      throw error;
    }
  }

  async function fetchSettings() {
    try {
      const result = await apiCall('appSetting');
      if (result.status === 'success' && result.data) {
        state.settings = result.data;
        injectCustomCss(state.settings.custom_css);
        
        // Show/hide "Saved for Later" section based on API setting
        if (elements.savedLaterSection) {
          // API returns string booleans, treat anything other than explicit "false" as enabled
          const saveForLaterEnabled = String(state.settings.save_for_later_option) !== 'false';
          elements.savedLaterSection.style.display = saveForLaterEnabled ? '' : 'none';
          
          // If "Saved for Later" is disabled and we're currently on that section, switch to wishlists
          if (!saveForLaterEnabled && state.currentSection === 'saved-for-later') {
            const wishlistKeys = Object.keys(state.wishlists);
            if (wishlistKeys.length > 0) {
              selectWishlist(wishlistKeys[0], 'wishlists');
            } else {
              state.currentSection = 'wishlists';
              showState('empty');
            }
          }
        }
        
        // Re-render products if they're already displayed to show/hide checkboxes based on settings
        if (state.currentWishlistName && elements.productsGrid && elements.productsGrid.children.length > 0) {
          renderProducts(state.currentWishlistName);
        }
        // Initialize compare button visibility after settings are loaded
        updateCompareButton();
        // Note: renderShareButtons() will be called after wishlist is selected in renderProducts()
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  }

  async function fetchWishlists(type = 'wishlist', skipAutoSelect = false) {
    try {
      const isSavedForLater = type === 'saved-for-later';
      const result = await apiCall('myWishlist', {
        saveforlater: isSavedForLater ? 'true' : 'false',
        product_id: ''
      });

      if (result.status === 'success' && result.data) {
        const productsData = result.data.wishlist_status || [];
        const wishlistsData = result.data.wishlists || {};

        if (isSavedForLater && wishlistsData['U0ZM']) {
          // Store saved for later separately
          state.savedForLaterProducts = productsData;
          state.savedForLaterCount = productsData.length;
          state.products = productsData;
          state.currentSection = 'saved-for-later';
          state.currentWishlistName = 'Saved for Later';
          renderSidebar();
          renderProducts('Saved for Later');
        } else if (isSavedForLater) {
          // No saved for later items
          state.savedForLaterProducts = [];
          state.savedForLaterCount = 0;
          state.products = [];
          state.currentSection = 'saved-for-later';
          state.currentWishlistName = 'Saved for Later';
          renderSidebar();
          showState('empty');
        } else {
          // Build images object
          const imagesData = {};
          Object.keys(wishlistsData).forEach(key => {
            if (key === 'U0ZM') return;
            const arr = [];
            productsData.forEach(item => {
              if (item.wishlist_id == wishlistsData[key]) {
                arr.push(item.image);
              }
            });
            imagesData[key] = arr;
          });

          // Filter out U0ZM
          const filteredWishlists = {};
          Object.keys(wishlistsData).forEach(key => {
            if (key !== 'U0ZM') {
              filteredWishlists[key] = wishlistsData[key];
            }
          });

          state.wishlists = filteredWishlists;
          state.products = productsData;
          state.images = imagesData;
          state.currentSection = 'wishlists';
          state.wishlistProductsLoaded = true;
          
          renderSidebar();
          
          // Auto-select first wishlist on page load (unless skipAutoSelect is true)
          if (!skipAutoSelect) {
            const wishlistKeys = Object.keys(filteredWishlists);
            if (wishlistKeys.length > 0) {
              selectWishlist(wishlistKeys[0], 'wishlists');
            } else {
              // No wishlists - show empty state
              renderSidebar(); // Make sure sidebar shows "No wishlists yet"
              showState('empty');
            }
          }
        }
      } else {
        // API returned no data
        renderSidebar(); // Make sure sidebar is rendered
        showState('empty');
      }
    } catch (error) {
      console.error('Failed to fetch wishlists:', error);
      showState('error');
    }
  }

  async function removeFromWishlist(variantId, handle) {
    try {
      let productHandle = handle;
      if (!productHandle && variantId) {
        const product = state.products.find(p => p.variant_id === variantId || p.var_id === variantId);
        if (product) {
          productHandle = product.handle;
          if (!productHandle && product.url) {
            const urlMatch = product.url.match(/\/products\/([^\/\?]+)/);
            if (urlMatch) {
              productHandle = urlMatch[1];
            }
          }
        }
      }
      
      await apiCall('removeWishlist', {
        variant_id: variantId || '',
        product_id: '',
        handle: productHandle || '',
        saveforlater: state.currentSection === 'saved-for-later' ? 'true' : 'false'
      });
      
      // Remove from compare selection if selected
      if (state.selectedCheckbox[variantId]) {
        delete state.selectedCheckbox[variantId];
      }
      
      // Refresh data
      await fetchWishlists(state.currentSection);
    } catch (error) {
      console.error('Failed to remove product:', error);
      alert('Failed to remove product. Please try again.');
    }
  }

  async function moveToCart(variantId, productId, quantity) {
    try {
      // First, remove from saved for later
      await apiCall('removeWishlist', {
        variant_id: variantId || '',
        product_id: productId || '',
        handle: '',
        saveforlater: 'true'
      });
      
      // Then add to cart
      const formData = {
        'items': [{
          'id': variantId,
          'quantity': parseInt(quantity) || 1
        }]
      };
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        // Redirect to cart page
        window.location.href = '/cart';
      } else {
        throw new Error('Failed to add to cart');
      }
    } catch (error) {
      console.error('Failed to move to cart:', error);
      alert('Failed to move product to cart. Please try again.');
    }
  }

  async function deleteAllProducts() {
    if (!confirm('Are you sure you want to delete all items?')) return;
    
    try {
      showState('loading');
      const deletePromises = state.products.map(product => 
        apiCall('removeWishlist', {
          variant_id: product.variant_id || product.var_id || '',
          product_id: '',
          handle: product.handle || '',
          saveforlater: state.currentSection === 'saved-for-later' ? 'true' : 'false'
        })
      );
      await Promise.all(deletePromises);
      await fetchWishlists(state.currentSection);
    } catch (error) {
      console.error('Failed to delete all:', error);
      alert('Failed to delete all items. Please try again.');
      showState('main');
    }
  }

  // === UI State Management ===
  function showState(stateName) {
    if (elements.states.loading) elements.states.loading.style.display = 'none';
    if (elements.states.empty) elements.states.empty.style.display = 'none';
    if (elements.states.error) elements.states.error.style.display = 'none';
    if (elements.productsWrapper) elements.productsWrapper.style.display = 'none';

    if (stateName === 'loading' && elements.states.loading) {
      elements.states.loading.style.display = 'flex';
    } else if (stateName === 'empty' && elements.states.empty) {
      // Update empty state message based on current section
      const emptyTitle = document.getElementById('gf-wishlist-empty-title');
      const emptyMessage = document.getElementById('gf-wishlist-empty-message');
      
      if (state.currentSection === 'saved-for-later') {
        if (emptyTitle) emptyTitle.textContent = 'No Items Saved for Later';
        if (emptyMessage) emptyMessage.textContent = 'You haven\'t saved any items for later yet. Add items to your wishlist and save them for later!';
      } else {
        if (emptyTitle) emptyTitle.textContent = 'Your Wishlist is Empty';
        if (emptyMessage) emptyMessage.textContent = 'Start adding items you love to your wishlist. You can access them anytime from any device!';
      }
      
      elements.states.empty.style.display = 'flex';
    } else if (stateName === 'error' && elements.states.error) {
      elements.states.error.style.display = 'flex';
    } else if (stateName === 'main' && elements.productsWrapper) {
      elements.productsWrapper.style.display = 'block';
    }
  }

  // === Sidebar Rendering ===
  function renderSidebar() {
    console.log('Rendering sidebar...', {
      wishlists: Object.keys(state.wishlists),
      currentWishlist: state.currentWishlistName,
      currentSection: state.currentSection
    });

    // Render wishlists (only show regular wishlists, not saved for later)
    if (elements.wishlistList) {
      const wishlistKeys = Object.keys(state.wishlists).filter(key => key !== 'Saved for Later');
      
      if (wishlistKeys.length === 0) {
        elements.wishlistList.innerHTML = '<p class="gf-wishlist-sidebar__empty">No wishlists yet</p>';
      } else {
        elements.wishlistList.innerHTML = wishlistKeys.map(key => {
          const isActive = state.currentWishlistName === key && state.currentSection === 'wishlists';
          const imageCount = (state.images[key] || []).length;
          return `
            <button type="button" 
                    class="gf-wishlist-sidebar__item ${isActive ? 'gf-wishlist-sidebar__item--active' : ''}" 
                    data-wishlist-name="${escapeHtml(key)}"
                    data-section="wishlists">
              <div class="gf-wishlist-sidebar__item-content">
                <span class="gf-wishlist-sidebar__item-name">${escapeHtml(key)}</span>
                <span class="gf-wishlist-sidebar__item-count">${imageCount}</span>
              </div>
            </button>
          `;
        }).join('');
      }
    } else {
      console.warn('wishlistList element not found');
    }

    // Render saved for later button (only if enabled via API setting)
    const saveForLaterEnabled = state.settings && String(state.settings.save_for_later_option) !== 'false';
    if (elements.savedLaterList && saveForLaterEnabled) {
      const isActive = state.currentSection === 'saved-for-later';
      elements.savedLaterList.innerHTML = `
        <button type="button" 
                class="gf-wishlist-sidebar__item ${isActive ? 'gf-wishlist-sidebar__item--active' : ''}" 
                data-wishlist-name="Saved for Later"
                data-section="saved-for-later">
          <div class="gf-wishlist-sidebar__item-content">
            <span class="gf-wishlist-sidebar__item-name">Saved for Later</span>
            <span class="gf-wishlist-sidebar__item-count">${state.savedForLaterCount}</span>
          </div>
        </button>
      `;
    } else if (elements.savedLaterList) {
      elements.savedLaterList.innerHTML = '';
    }
  }

  // === Product Rendering ===
  function renderProducts(wishlistName) {
    if (!elements.productsGrid) {
      console.warn('productsGrid element not found');
      return;
    }

    let products = [];
    
    if (state.currentSection === 'saved-for-later') {
      products = state.products;
    } else {
      const wishlistId = state.wishlists[wishlistName];
      if (!wishlistId) {
        console.warn('Wishlist ID not found for:', wishlistName);
        showState('empty');
        return;
      }
      products = state.products.filter(p => p.wishlist_id == wishlistId);
    }

    console.log('Rendering products for:', wishlistName, 'Count:', products.length);

    if (products.length === 0) {
      showState('empty');
      return;
    }

    // Update title
    if (elements.productsTitle) {
      elements.productsTitle.textContent = wishlistName;
    }

    // Show/hide header actions
    const headerActions = document.getElementById('gf-wishlist-header-actions');
    if (headerActions) {
      headerActions.style.display = products.length > 0 ? 'flex' : 'none';
    }
    if (elements.deleteAllBtn) {
      elements.deleteAllBtn.style.display = products.length > 0 ? 'block' : 'none';
    }

    // Render share buttons
    renderShareButtons();

    // Render products
    elements.productsGrid.innerHTML = products.map(product => {
      let productHandle = product.handle;
      if (!productHandle && product.url) {
        const urlMatch = product.url.match(/\/products\/([^\/\?]+)/);
        if (urlMatch) {
          productHandle = urlMatch[1];
        }
      }
      
      const variantId = product.variant_id || product.var_id;
      const isChecked = state.selectedCheckbox[variantId] ? 'checked' : '';
      const showCompare = state.settings && state.settings.compare_products_option !== "false";
      
      return `
        <div class="gridboxChild" style="position: relative;">
          ${showCompare ? `
            <input style="position: absolute; top: 5px; right: 5px; z-index: 10; width: 20px; height: 20px; cursor: pointer;" 
                   onChange="window.GemfindWishlistPage.toggleCompareSelection('${escapeHtml(variantId)}', '${escapeHtml(product.quantity || 1)}')" 
                   class="gf--input-selection" 
                   type="checkbox" 
                   value="${escapeHtml(variantId)}" 
                   ${isChecked} />
          ` : ''}
          <div class="gf-wishlist-pro-quantity">Quantity: ${product.quantity || 1}</div>
          <div class="productimg">
            <img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}">
          </div>
          <h2 class="Product_title">${escapeHtml(product.title)}</h2>
          <h3 class="Product_price">$${escapeHtml(product.price)}</h3>
          <span class="Productstock ${product.stock_status === 'In Stock' ? 'instock' : 'outofstock'}">
            ${escapeHtml(product.stock_status)}
          </span>
          <div class="addtocartbtn gf-card-footer">
            <div style="position: relative;">
              <a href="${escapeHtml(product.url || `/products/${productHandle || ''}`)}">View More</a>
            </div>
            ${state.currentSection === 'saved-for-later' ? `
              <button type="button" 
                      onclick="window.GemfindWishlistPage.moveToCart('${escapeHtml(variantId)}', '${escapeHtml(product.id || '')}', '${escapeHtml(product.quantity || 1)}')" 
                      class="gf-remove-btn">
                Move To Cart
              </button>
            ` : `
              <button type="button" 
                      onclick="window.GemfindWishlistPage.removeProduct('${escapeHtml(variantId)}', '${escapeHtml(productHandle || '')}')" 
                      class="gf-remove-btn">
                Remove
              </button>
            `}
          </div>
        </div>
      `;
    }).join('');

    // Update compare button visibility after rendering
    setTimeout(() => {
      updateCompareButton();
    }, 100);

    showState('main');
  }

  // === Compare Products Functions ===
  function toggleCompareSelection(variantId, quantity) {
    if (state.selectedCheckbox[variantId]) {
      delete state.selectedCheckbox[variantId];
    } else {
      state.selectedCheckbox[variantId] = quantity;
    }
    updateCompareButton();
    
    // Update checkbox state in DOM
    const checkbox = document.querySelector(`input[value="${variantId}"]`);
    if (checkbox) {
      checkbox.checked = !!state.selectedCheckbox[variantId];
    }
  }

  function updateCompareButton() {
    const compareBtn = document.getElementById('gf-wishlist-compare-btn');
    const compareCount = document.getElementById('gf-wishlist-compare-count');
    const selectedCount = Object.keys(state.selectedCheckbox).length;
    const showCompare = state.settings && state.settings.compare_products_option !== "false";
    
    if (compareBtn) {
      if (showCompare && selectedCount > 0) {
        compareBtn.style.display = 'flex';
        if (compareCount) {
          compareCount.textContent = selectedCount;
        }
      } else {
        compareBtn.style.display = 'none';
      }
    }
  }

  function compareProducts() {
    const selectedCount = Object.keys(state.selectedCheckbox).length;
    
    if (selectedCount > 3) {
      alert("You can compare max 3 products.");
      return;
    } else if (selectedCount <= 1) {
      alert("You can compare min 2 products.");
      return;
    }
    
    state.compareModalOpen = true;
    renderCompareModal();
  }

  function removeProductsCompare(variantId) {
    delete state.selectedCheckbox[variantId];
    updateCompareButton();
    
    // Update checkbox state in DOM
    const checkbox = document.querySelector(`input[value="${variantId}"]`);
    if (checkbox) {
      checkbox.checked = false;
    }
    
    // Close modal if only 1 or 0 products selected
    setTimeout(() => {
      if (Object.keys(state.selectedCheckbox).length <= 1) {
        closeCompareModal();
      } else {
        renderCompareModal();
      }
    }, 100);
  }

  function renderCompareModal() {
    const selectedProducts = Object.keys(state.selectedCheckbox).map(key =>
      state.products.find(item => (item.variant_id || item.var_id) == key)
    ).filter(item => item !== undefined);

    if (selectedProducts.length === 0) {
      closeCompareModal();
      return;
    }

    // Create or get modal
    let modal = document.getElementById('gf-compare-modal');
    if (!modal) {
      modal = document.createElement('dialog');
      modal.id = 'gf-compare-modal';
      modal.className = 'gf-wishlist-dialog compareProduct--modal';
      document.body.appendChild(modal);
      
      // Close on backdrop click
      modal.addEventListener('click', (e) => {
        if (e.target === modal) {
          closeCompareModal();
        }
      });
      
      // Close on Escape
      modal.addEventListener('cancel', (e) => {
        e.preventDefault();
        closeCompareModal();
      });
    }

    modal.innerHTML = `
      <button class="gf-dialog-close" onclick="window.GemfindWishlistPage.closeCompareModal()">×</button>
      <div class="gf-compare-pro-header">
        <h3>Compare Products</h3>
      </div>
      <div class="gf--com-pro-container" style="margin-top: 0;">
        <table class="gf-compare-table" border="1px" style="margin-top: 20px;">
          <thead>
            <tr>
              ${selectedProducts.map(item => `
                <th>
                  <div class="gf-compare-product-header">
                    <div class="gf--compare-product-img">
                      <button onclick="window.GemfindWishlistPage.removeProductsCompare('${escapeHtml(item.variant_id || item.var_id)}')" class="gf-compare-product-remove">×</button>
                      <img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}">
                    </div>
                    <h4>${escapeHtml(item.title)}</h4>
                    <p>$${escapeHtml(item.price)}</p>
                  </div>
                </th>
              `).join('')}
            </tr>
          </thead>
          <tbody>
            <tr><td colspan="${selectedProducts.length}">Available</td></tr>
            <tr>
              ${selectedProducts.map(item => `
                <td>${item.quantity > 0 ? "Yes" : 'No'}</td>
              `).join('')}
            </tr>
            <tr><td colspan="${selectedProducts.length}">Product Type</td></tr>
            <tr>
              ${selectedProducts.map(item => `
                <td>${item.productType || '-'}</td>
              `).join('')}
            </tr>
            <tr><td colspan="${selectedProducts.length}">SKU</td></tr>
            <tr>
              ${selectedProducts.map(item => `
                <td>${item.sku || '-'}</td>
              `).join('')}
            </tr>
            <tr><td colspan="${selectedProducts.length}">Available Options</td></tr>
            <tr>
              ${selectedProducts.map(item => `
                <td>${item.totalVariants || '-'}</td>
              `).join('')}
            </tr>
            <tr><td colspan="${selectedProducts.length}">Category</td></tr>
            <tr>
              ${selectedProducts.map(item => `
                <td>${item.productCategory || '-'}</td>
              `).join('')}
            </tr>
            <tr><td colspan="${selectedProducts.length}">Vendor</td></tr>
            <tr>
              ${selectedProducts.map(item => `
                <td>${item.vendor || '-'}</td>
              `).join('')}
            </tr>
          </tbody>
        </table>
      </div>
    `;

    requestAnimationFrame(() => {
      modal.showModal();
    });
  }

  function closeCompareModal() {
    const modal = document.getElementById('gf-compare-modal');
    if (modal) {
      modal.close();
      state.compareModalOpen = false;
    }
  }

  // === Share Wishlist Functions ===
  function getShareUrl() {
    const customerId = getCustomerId();
    if (!customerId) return null;
    const encodedId = btoa(customerId.toString());
    const shopDomain = getShopDomain();
    // Ensure we use https:// protocol
    const protocol = window.location.protocol === 'https:' ? 'https://' : 'https://';
    return `${protocol}${shopDomain}/pages/gemfind-my-wishlist?wid=${encodedId}`;
  }

  function shareOnSocial(social) {
    const shareUrl = getShareUrl();
    if (!shareUrl) {
      alert('Please log in to share your wishlist');
      return;
    }

    if (social === 'facebook') {
      window.open('https://www.facebook.com/sharer.php?u=' + encodeURIComponent(shareUrl), '_blank');
    } else if (social === 'twitter') {
      window.open('http://twitter.com/share?url=' + encodeURIComponent(shareUrl), '_blank');
    } else if (social === 'copy') {
      navigator.clipboard.writeText(shareUrl).then(() => {
        showToast('Link copied to clipboard!');
      }, () => {
        alert('Failed to copy link');
      });
    } else if (social === 'mail') {
      openShareModal();
    }
  }

  function openShareModal() {
    state.shareModalOpen = true;
    renderShareModal();
  }

  function closeShareModal() {
    const modal = document.getElementById('gf-share-modal');
    if (modal) {
      modal.close();
      state.shareModalOpen = false;
      // Reset form
      state.shareFormData = {
        senderName: '',
        recipientsMail: '',
        message: '',
        reCaptchaValue: ''
      };
      // Reset reCAPTCHA if exists
      if (state.reCaptchaWidgetId !== null && window.GemfindWishlistCommon) {
        window.GemfindWishlistCommon.resetRecaptcha(state.reCaptchaWidgetId);
        state.reCaptchaWidgetId = null;
      }
    }
  }

  async function renderShareModal() {
    const customerId = getCustomerId();
    const urlParams = new URLSearchParams(window.location.search);
    const wid = urlParams.get('wid');
    
    // Only show share if customer is logged in and not viewing a shared wishlist
    // If wid exists in URL, it means we're viewing a shared wishlist, so don't show share
    if (!customerId || wid) {
      return;
    }

    // Check if plan allows sharing (plan > 1)
    if (!state.settings || !state.settings.plan || state.settings.plan <= 1) {
      return;
    }

    let modal = document.getElementById('gf-share-modal');
    if (!modal) {
      const { dialog, closeBtn } = window.GemfindWishlistCommon.createDialog('share--modal');
      dialog.id = 'gf-share-modal';
      document.body.appendChild(dialog);
      modal = dialog;
    }

    const siteKey = state.settings.site_key || null;
    const hasRecaptcha = siteKey !== null && siteKey !== '';

    modal.innerHTML = `
      <div style="padding: 30px; max-width: 500px;">
        <div style="margin-bottom: 20px;">
          <h3 style="margin: 0 0 10px 0; font-size: 20px; font-weight: 600; color: #000; font-family: var(--gf-font-family);">Share Wishlist Via Email</h3>
          <p style="margin: 0; font-size: 14px; color: #666; font-family: var(--gf-font-family);">Send your wishlist to a friend or family member</p>
        </div>
        <form class="share-email-form">
          <div class="form-field" style="display: flex; flex-direction: column; gap: 15px;">
            <div>
              <label for="share-recipients-name" style="display: block; margin-bottom: 6px; font-size: 14px; font-weight: 500; color: #000; font-family: var(--gf-font-family);">Recipient's Name</label>
              <input 
                type="text" 
                id="share-recipients-name" 
                placeholder="Enter recipient's name" 
                value="${escapeHtml(state.shareFormData.senderName)}"
                style="padding: 12px; border: 1px solid #ddd; border-radius: 6px; font-size: 14px; width: 100%; font-family: var(--gf-font-family); transition: border-color 0.2s;"
                onfocus="this.style.borderColor='#000'; this.style.outline='none';"
                onblur="this.style.borderColor='#ddd';"
              />
            </div>
            <div>
              <label for="share-recipients-email" style="display: block; margin-bottom: 6px; font-size: 14px; font-weight: 500; color: #000; font-family: var(--gf-font-family);">Recipient's Email <span style="color: #ff0000;">*</span></label>
              <input 
                type="email" 
                id="share-recipients-email" 
                placeholder="Enter recipient's email address" 
                required
                value="${escapeHtml(state.shareFormData.recipientsMail)}"
                style="padding: 12px; border: 1px solid #ddd; border-radius: 6px; font-size: 14px; width: 100%; font-family: var(--gf-font-family); transition: border-color 0.2s;"
                onfocus="this.style.borderColor='#000'; this.style.outline='none';"
                onblur="this.style.borderColor='#ddd';"
              />
            </div>
            <div>
              <label for="share-message" style="display: block; margin-bottom: 6px; font-size: 14px; font-weight: 500; color: #000; font-family: var(--gf-font-family);">Personal Message <span style="font-weight: 400; color: #666; font-size: 12px;">(Optional)</span></label>
              <textarea 
                id="share-message" 
                placeholder="Add a personal message..." 
                rows="4"
                value="${escapeHtml(state.shareFormData.message)}"
                style="padding: 12px; border: 1px solid #ddd; border-radius: 6px; font-size: 14px; width: 100%; font-family: var(--gf-font-family); resize: vertical; transition: border-color 0.2s;"
                onfocus="this.style.borderColor='#000'; this.style.outline='none';"
                onblur="this.style.borderColor='#ddd';"
              >${escapeHtml(state.shareFormData.message)}</textarea>
            </div>
            ${hasRecaptcha ? `
              <div id="share-recaptcha-container" style="margin-top: 5px;"></div>
            ` : ''}
            <div id="share-email-error" style="color: #ff0000; font-size: 13px; margin-top: -10px; display: none;">
              Please enter a valid email address
            </div>
            <div style="margin-top: 10px; display: flex; gap: 10px; justify-content: flex-end;">
              <button
                type="button"
                onclick="window.GemfindWishlistPage.closeShareModal()"
                style="background-color: transparent; color: #666; padding: 12px 24px; border: 1px solid #ddd; border-radius: 6px; cursor: pointer; font-family: var(--gf-font-family); font-size: 15px; font-weight: 500; transition: all 0.2s;"
                onmouseover="this.style.borderColor='#999'; this.style.color='#000';"
                onmouseout="this.style.borderColor='#ddd'; this.style.color='#666';"
              >
                Cancel
              </button>
              <button
                type="button"
                id="share-submit-btn"
                class="btn button preference-btn"
                style="background-color: #000; color: #fff; padding: 12px 35px; border: none; border-radius: 6px; cursor: pointer; font-family: var(--gf-font-family); font-size: 15px; font-weight: 600; transition: all 0.2s;"
                onmouseover="this.style.backgroundColor='#333';"
                onmouseout="this.style.backgroundColor='#000';"
              >
                Send Wishlist
              </button>
            </div>
          </div>
        </form>
      </div>
    `;

    // Add close button
    const closeBtn = modal.querySelector('.gf-dialog-close');
    if (closeBtn) {
      closeBtn.onclick = closeShareModal;
    }

    // Setup form handlers
    const senderNameInput = document.getElementById('share-recipients-name');
    const recipientsEmailInput = document.getElementById('share-recipients-email');
    const messageInput = document.getElementById('share-message');
    const submitBtn = document.getElementById('share-submit-btn');
    const errorDiv = document.getElementById('share-email-error');

    if (senderNameInput) {
      senderNameInput.addEventListener('input', (e) => {
        state.shareFormData.senderName = e.target.value;
      });
    }

    if (recipientsEmailInput) {
      recipientsEmailInput.addEventListener('input', (e) => {
        state.shareFormData.recipientsMail = e.target.value;
        if (errorDiv) errorDiv.style.display = 'none';
      });
    }

    if (messageInput) {
      messageInput.addEventListener('input', (e) => {
        state.shareFormData.message = e.target.value;
      });
    }

    // Setup reCAPTCHA if needed
    if (hasRecaptcha) {
      const recaptchaContainer = document.getElementById('share-recaptcha-container');
      if (recaptchaContainer && window.GemfindWishlistCommon) {
        window.GemfindWishlistCommon.renderRecaptcha(recaptchaContainer, siteKey, (value) => {
          state.shareFormData.reCaptchaValue = value;
          if (submitBtn) {
            submitBtn.disabled = false;
          }
        }).then((widgetId) => {
          state.reCaptchaWidgetId = widgetId;
          if (submitBtn) {
            submitBtn.disabled = true;
          }
        }).catch((error) => {
          console.error('Failed to render reCAPTCHA:', error);
        });
      }
    }

    // Setup submit handler
    if (submitBtn) {
      submitBtn.addEventListener('click', async () => {
        const isValidEmail = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/g;
        
        if (!state.shareFormData.recipientsMail || !state.shareFormData.recipientsMail.match(isValidEmail)) {
          if (errorDiv) errorDiv.style.display = 'block';
          return;
        }

        if (hasRecaptcha && !state.shareFormData.reCaptchaValue) {
          alert('Please complete the reCAPTCHA verification');
          return;
        }

        await sendShareEmail();
      });
    }

    requestAnimationFrame(() => {
      modal.showModal();
    });
  }

  async function sendShareEmail() {
    const shareUrl = getShareUrl();
    if (!shareUrl) {
      alert('Unable to generate share URL');
      return;
    }

    try {
      const submitBtn = document.getElementById('share-submit-btn');
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Sending...';
      }

      const result = await apiCall('sharewishlist', {
        sender: state.shareFormData.senderName,
        recipientsMail: state.shareFormData.recipientsMail,
        message: state.shareFormData.message,
        wishlist_id: state.currentWishlistId || state.currentWishlistName,
        title: 'Share your wishlist',
        url: shareUrl
      });

      if (result.status === 'success') {
        showToast('Email Sent Successfully!');
        closeShareModal();
      } else {
        showToast('Email Could not be sent');
      }
    } catch (error) {
      console.error('Failed to send share email:', error);
      showToast('Email Could not be sent');
    } finally {
      const submitBtn = document.getElementById('share-submit-btn');
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.innerHTML = 'Send Wishlist';
      }
    }
  }

  function showToast(message) {
    // Simple toast notification
    let toast = document.getElementById('gf-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'gf-toast';
      toast.style.cssText = 'position: fixed; bottom: 20px; right: 20px; background: #000; color: #fff; padding: 15px 25px; border-radius: 4px; z-index: 10000; font-family: var(--gf-font-family); font-size: 14px; box-shadow: 0 4px 12px rgba(0,0,0,0.3);';
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.style.display = 'block';
    setTimeout(() => {
      toast.style.display = 'none';
    }, 3000);
  }

  function renderShareButtons() {
    const customerId = getCustomerId();
    const urlParams = new URLSearchParams(window.location.search);
    const wid = urlParams.get('wid');
    
    // Only show share if customer is logged in and not viewing a shared wishlist
    // If wid exists in URL, it means we're viewing a shared wishlist, so don't show share
    const shouldShow = customerId && !wid;
    // Plan must be greater than 1 (plan 2 or 3) to show share feature
    const planAllows = state.settings && state.settings.plan && parseInt(state.settings.plan) > 1;
    const isWishlistSelected = state.currentWishlistName && state.currentSection === 'wishlists';

    const shareContainer = document.getElementById('gf-wishlist-share');
    if (!shareContainer) {
      console.warn('Share container not found');
      return;
    }

    // Always log debug info to help troubleshoot
    console.log('🔍 Share buttons check:', {
      customerId: !!customerId,
      customerIdValue: customerId,
      wid: wid,
      shouldShow: shouldShow,
      plan: state.settings?.plan,
      planAllows: planAllows,
      isWishlistSelected: isWishlistSelected,
      currentWishlistName: state.currentWishlistName,
      currentSection: state.currentSection,
      settingsExists: !!state.settings,
      shareContainerExists: !!shareContainer
    });

    if (shouldShow && planAllows && isWishlistSelected) {
      console.log('✅ Showing share buttons');
      shareContainer.style.display = 'flex';
      shareContainer.innerHTML = `
        <p style="margin: 0; margin-right: 10px; font-family: var(--gf-font-family); font-size: 14px;">Share On</p>
        <button type="button" onclick="window.GemfindWishlistPage.shareOnSocial('facebook')" style="background: transparent; border: 0; padding: 0; cursor: pointer;">
          <svg class="facebookcontainer" width="25" height="25" viewBox="0 0 24 24" fill="#3b5998">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
          </svg>
        </button>
        <button type="button" onclick="window.GemfindWishlistPage.shareOnSocial('twitter')" style="background: transparent; border: 0; padding: 0; cursor: pointer;">
          <svg class="twittercontainer" width="25" height="25" viewBox="0 0 24 24" fill="#00acee">
            <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
          </svg>
        </button>
        <button type="button" onclick="window.GemfindWishlistPage.shareOnSocial('mail')" style="background: transparent; border: 0; padding: 0; cursor: pointer;">
          <svg class="mailcontainer" width="25" height="25" viewBox="0 0 24 24" fill="#777">
            <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
          </svg>
        </button>
      `;
    } else {
      console.log('❌ Hiding share buttons - conditions not met');
      shareContainer.style.display = 'none';
    }
  }

  // === Wishlist Selection ===
  async function selectWishlist(wishlistName, section) {
    // Prevent selecting "Saved for Later" if it's disabled
    if (section === 'saved-for-later') {
      const saveForLaterEnabled = state.settings && String(state.settings.save_for_later_option) !== 'false';
      if (!saveForLaterEnabled) {
        console.warn('Saved for Later section is disabled');
        return;
      }
    }
    
    state.currentWishlistName = wishlistName;
    state.currentSection = section;
    
    if (section === 'saved-for-later') {
      // Fetch saved for later data
      showState('loading');
      await fetchWishlists('saved-for-later');
    } else {
      // Switching to wishlist section
      // If we don't have wishlist products loaded (e.g., switched from saved-for-later), re-fetch
      if (!state.wishlistProductsLoaded || state.products.length === 0 || Object.keys(state.wishlists).length === 0) {
        console.log('Re-fetching wishlist data for:', wishlistName);
        showState('loading');
        await fetchWishlists('wishlist', true); // Skip auto-select
        // After fetching, select the specific wishlist
        if (state.wishlists[wishlistName]) {
          state.currentWishlistId = state.wishlists[wishlistName];
          renderProducts(wishlistName);
          renderSidebar(); // Update active state
        } else {
          console.warn('Wishlist not found after fetch:', wishlistName);
          showState('empty');
        }
      } else {
        // We have the data, just render
        state.currentWishlistId = state.wishlists[wishlistName];
        if (!state.currentWishlistId) {
          console.warn('Wishlist ID not found for:', wishlistName);
          showState('empty');
          return;
        }
        renderProducts(wishlistName);
        renderSidebar(); // Update active state
      }
    }
  }

  // === Global API ===
  window.GemfindWishlistPage = {
    selectWishlist: selectWishlist,
    removeProduct: (variantId, handle) => removeFromWishlist(variantId, handle),
    moveToCart: (variantId, productId, quantity) => moveToCart(variantId, productId, quantity),
    deleteAll: deleteAllProducts,
    toggleCompareSelection: toggleCompareSelection,
    compareProducts: compareProducts,
    removeProductsCompare: removeProductsCompare,
    closeCompareModal: closeCompareModal,
    shareOnSocial: shareOnSocial,
    openShareModal: openShareModal,
    closeShareModal: closeShareModal
  };

  // === Expand/Collapse Functions ===
  function toggleWishlistSection() {
    const header = document.getElementById('gf-wishlist-header');
    const section = header?.closest('.gf-wishlist-sidebar__section');
    if (!header || !section) return;

    const isExpanded = header.getAttribute('aria-expanded') === 'true';
    header.setAttribute('aria-expanded', !isExpanded);
    section.setAttribute('data-collapsed', isExpanded);
  }

  // === Event Listeners ===
  function initEventListeners() {
    // Expand/collapse header for wishlists section
    const wishlistHeader = document.getElementById('gf-wishlist-header');
    if (wishlistHeader) {
      wishlistHeader.addEventListener('click', (e) => {
        // Prevent event from bubbling to item clicks
        e.stopPropagation();
        toggleWishlistSection();
      });
    }

    // Sidebar item clicks - ensure only one is selected
    if (elements.wishlistList) {
      elements.wishlistList.addEventListener('click', (e) => {
        const item = e.target.closest('.gf-wishlist-sidebar__item');
        if (item) {
          // Remove active state from all items first
          document.querySelectorAll('.gf-wishlist-sidebar__item').forEach(el => {
            el.classList.remove('gf-wishlist-sidebar__item--active');
          });
          
          // Add active state to clicked item
          item.classList.add('gf-wishlist-sidebar__item--active');
          
          const wishlistName = item.getAttribute('data-wishlist-name');
          const section = item.getAttribute('data-section');
          selectWishlist(wishlistName, section);
        }
      });
    }

    if (elements.savedLaterList) {
      elements.savedLaterList.addEventListener('click', (e) => {
        const item = e.target.closest('.gf-wishlist-sidebar__item');
        if (item) {
          // Remove active state from all items first
          document.querySelectorAll('.gf-wishlist-sidebar__item').forEach(el => {
            el.classList.remove('gf-wishlist-sidebar__item--active');
          });
          
          // Add active state to clicked item
          item.classList.add('gf-wishlist-sidebar__item--active');
          
          const wishlistName = item.getAttribute('data-wishlist-name');
          const section = item.getAttribute('data-section');
          selectWishlist(wishlistName, section);
        }
      });
    }

    // Delete all button
    if (elements.deleteAllBtn) {
      elements.deleteAllBtn.addEventListener('click', deleteAllProducts);
    }

    // Compare products button
    const compareBtn = document.getElementById('gf-wishlist-compare-btn');
    if (compareBtn) {
      compareBtn.addEventListener('click', compareProducts);
    }
  }

  // === Initialization ===
  async function init() {
    // Re-query elements in case DOM wasn't ready
    elements.container = document.querySelector(CONFIG.selectors.container);
    elements.sidebar = document.querySelector(CONFIG.selectors.sidebar);
    elements.wishlistList = document.querySelector(CONFIG.selectors.wishlistList);
    elements.savedLaterList = document.querySelector(CONFIG.selectors.savedLaterList);
    elements.mainContent = document.querySelector(CONFIG.selectors.mainContent);
    elements.productsWrapper = document.querySelector(CONFIG.selectors.productsWrapper);
    elements.productsTitle = document.querySelector(CONFIG.selectors.productsTitle);
    elements.productsGrid = document.querySelector(CONFIG.selectors.productsGrid);
    elements.deleteAllBtn = document.querySelector(CONFIG.selectors.deleteAllBtn);
    elements.states.loading = document.querySelector(CONFIG.selectors.states.loading);
    elements.states.empty = document.querySelector(CONFIG.selectors.states.empty);
    elements.states.error = document.querySelector(CONFIG.selectors.states.error);
    elements.config = document.querySelector(CONFIG.selectors.config);

    if (!elements.container) {
      console.warn('Wishlist page container not found. Selector:', CONFIG.selectors.container);
      return;
    }

    console.log('Wishlist page initializing...', {
      container: !!elements.container,
      sidebar: !!elements.sidebar,
      wishlistList: !!elements.wishlistList,
      productsGrid: !!elements.productsGrid
    });

    // Show initial loading state in sidebar
    if (elements.wishlistList) {
      elements.wishlistList.innerHTML = '<p class="gf-wishlist-sidebar__empty">Loading...</p>';
    }
    if (elements.savedLaterList) {
      elements.savedLaterList.innerHTML = '<p class="gf-wishlist-sidebar__empty">Loading...</p>';
    }

    initEventListeners();
    await fetchSettings();
    
    // Load initial data
    showState('loading');
    await fetchWishlists('wishlist');
    
    // Also fetch saved for later count if enabled (for sidebar display)
    const saveForLaterEnabled = state.settings && String(state.settings.save_for_later_option) !== 'false';
    if (saveForLaterEnabled) {
      try {
        const result = await apiCall('myWishlist', {
          saveforlater: 'true',
          product_id: ''
        });
        if (result.status === 'success' && result.data) {
          const productsData = result.data.wishlist_status || [];
          state.savedForLaterCount = productsData.length;
          renderSidebar(); // Update count in sidebar
        }
      } catch (error) {
        console.error('Failed to fetch saved for later count:', error);
      }
    }
  }

  // Start when DOM is ready - use multiple strategies
  function startInit() {
    // Try immediate init
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      // DOM already loaded, but wait a bit for Shopify to inject blocks
      setTimeout(init, 100);
    }
  }

  startInit();

})();
